import { StrictMode, Suspense, lazy } from "react";
import { createRoot } from "react-dom/client";
import { Navigate, RouterProvider, createBrowserRouter } from "react-router";
import "./styles/globals.css";

// Theme bootstrap (before render to avoid flash)
(() => {
  const stored = localStorage.getItem("ip_theme") as "system" | "light" | "dark" | null;
  const theme = stored ?? "light";
  const resolved =
    theme === "system"
      ? window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light"
      : theme;
  if (resolved === "dark") {
    document.documentElement.classList.add("dark");
  }
})();

import { AppShell } from "./components/layout/AppShell";
import { RouteErrorFallback } from "./components/layout/RouteErrorFallback";
import { isInvoiceOnlyScope } from "./lib/deployment/public-scope";
import { VerifiedEntitlementProvider } from "./lib/pricing/purchase-verification-provider";
import { SEO_LANDING_PAGES, SeoLandingPage } from "./routes/SeoLanding";

const LegalPage = lazy(() =>
  import("./routes/Legal").then((module) => ({ default: module.LegalPage })),
);
const NotFound = lazy(() =>
  import("./routes/NotFound").then((module) => ({ default: module.NotFound })),
);
const Pricing = lazy(() =>
  import("./routes/Pricing").then((module) => ({ default: module.Pricing })),
);
const BrandKit = lazy(() =>
  import("./routes/app/BrandKit").then((module) => ({ default: module.BrandKit })),
);
const BulkInvoices = lazy(() =>
  import("./routes/app/BulkInvoices").then((module) => ({ default: module.BulkInvoices })),
);
const Catalog = lazy(() =>
  import("./routes/app/Catalog").then((module) => ({ default: module.Catalog })),
);
const Customers = lazy(() =>
  import("./routes/app/Customers").then((module) => ({ default: module.Customers })),
);
const Dashboard = lazy(() =>
  import("./routes/app/Dashboard").then((module) => ({ default: module.Dashboard })),
);
const InvoiceDetail = lazy(() =>
  import("./routes/app/InvoiceDetail").then((module) => ({ default: module.InvoiceDetail })),
);
const NewInvoice = lazy(() =>
  import("./routes/app/NewInvoice").then((module) => ({ default: module.NewInvoice })),
);
const HelpPage = lazy(() =>
  import("./routes/app/PlaceholderPages").then((module) => ({ default: module.HelpPage })),
);
const Settings = lazy(() =>
  import("./routes/app/Settings").then((module) => ({ default: module.Settings })),
);
const TemplateDesigner = lazy(() =>
  import("./routes/app/TemplateDesigner").then((module) => ({ default: module.TemplateDesigner })),
);
const InvoiceDesigns = lazy(() =>
  import("./routes/app/Templates").then((module) => ({ default: module.InvoiceDesigns })),
);
const WorkTemplates = lazy(() =>
  import("./routes/app/WorkTemplates").then((module) => ({ default: module.WorkTemplates })),
);
const DevColors = lazy(() =>
  import("./routes/dev/Colors").then((module) => ({ default: module.DevColors })),
);

const invoiceOnly = isInvoiceOnlyScope();
const disabledRoute = <Navigate to="/app/" replace />;
const routeFallback = <div className="min-h-screen bg-surface" />;
const routeErrorElement = <RouteErrorFallback />;

function route(element: React.ReactNode) {
  return <Suspense fallback={routeFallback}>{element}</Suspense>;
}

const router = createBrowserRouter([
  // Public
  { path: "/", element: <Navigate to="/app/" replace />, errorElement: routeErrorElement },
  ...SEO_LANDING_PAGES.map((page) => ({
    path: page.path,
    element: route(<SeoLandingPage page={page} />),
    errorElement: routeErrorElement,
  })),
  {
    path: "/pricing",
    element: invoiceOnly ? disabledRoute : route(<Pricing />),
    errorElement: routeErrorElement,
  },
  { path: "/legal/:slug", element: route(<LegalPage />), errorElement: routeErrorElement },

  // Dev
  { path: "/dev/colors", element: route(<DevColors />), errorElement: routeErrorElement },

  // App
  {
    path: "/app",
    element: <AppShell />,
    errorElement: routeErrorElement,
    children: [
      { index: true, element: route(<Dashboard />) },
      {
        path: "onboarding",
        element: <Navigate to={invoiceOnly ? "/app/new?type=invoice" : "/app/brand-kit"} replace />,
      },
      { path: "new", element: route(<NewInvoice />) },
      { path: "bulk", element: invoiceOnly ? disabledRoute : route(<BulkInvoices />) },
      { path: "invoice/:id", element: route(<InvoiceDetail />) },
      { path: "customers", element: route(<Customers />) },
      { path: "catalog", element: route(<Catalog />) },
      { path: "templates", element: invoiceOnly ? disabledRoute : route(<WorkTemplates />) },
      { path: "invoice-designs", element: invoiceOnly ? disabledRoute : route(<InvoiceDesigns />) },
      { path: "designer", element: invoiceOnly ? disabledRoute : route(<TemplateDesigner />) },
      {
        path: "designer/:templateId",
        element: invoiceOnly ? disabledRoute : route(<TemplateDesigner />),
      },
      { path: "brand-kit", element: invoiceOnly ? disabledRoute : route(<BrandKit />) },
      { path: "settings", element: route(<Settings />) },
      { path: "help", element: route(<HelpPage />) },
    ],
  },

  { path: "*", element: route(<NotFound />), errorElement: routeErrorElement },
]);

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <VerifiedEntitlementProvider>
      <RouterProvider router={router} />
    </VerifiedEntitlementProvider>
  </StrictMode>,
);
